import { RSAA } from 'redux-api-middleware';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { restApiCaller } from '@makemydeal/dr-shared-network';
import type { InsuranceProviderOption } from '@makemydeal/dr-shared-components';
import type {
    CustomerCardSeedPayload,
    CustomerCardStoreState,
    CustomerFormValues,
    RawInsuranceProvider
} from '../types/CustomerCardTypes';
import type { SaveParticipantsPayload } from '../mappers/participantsMapper';
import type { SyncCustomerData } from '../mappers/syncCustomerMapper';
import type { CustomerSpdSaveItem } from '../types/customerSpd.types';
import { getDealerId, getDealId, getInsuranceProviders } from './selectors';
import type { VehicleInsurance } from '@makemydeal/dr-platform-types';
import { logCustomerCardError, logCustomerCardInsuranceFormEvent } from '../utils/newRelicLogging';
import {
    SEED_CUSTOMER_CARD,
    CUSTOMER_CARD_APPLY_STARTED,
    CUSTOMER_CARD_APPLY_SUCCEEDED,
    CUSTOMER_CARD_APPLY_FAILED,
    CUSTOMER_CARD_CANCELLED,
    SET_INSURANCE_PROVIDERS,
    SAVE_PARTICIPANTS_REQUEST,
    SAVE_PARTICIPANTS_SUCCESS,
    SAVE_PARTICIPANTS_FAILURE,
    SYNC_CUSTOMER_REQUEST,
    SYNC_CUSTOMER_SUCCESS,
    SYNC_CUSTOMER_FAILURE,
    SAVE_INSURANCE_REQUEST,
    SAVE_INSURANCE_SUCCESS,
    SAVE_INSURANCE_FAILURE
} from './actions';

export const seedCustomerCard = (payload: CustomerCardSeedPayload) =>
    ({
        type: SEED_CUSTOMER_CARD,
        payload
    } as const);

export const customerCardApplyStarted = () =>
    ({
        type: CUSTOMER_CARD_APPLY_STARTED
    } as const);

export const customerCardApplySucceeded = (
    buyer?: CustomerFormValues,
    coBuyer?: CustomerFormValues | null,
    insurance?: VehicleInsurance | null
) =>
    ({
        type: CUSTOMER_CARD_APPLY_SUCCEEDED,
        payload: { buyer, coBuyer, insurance }
    } as const);

export const customerCardApplyFailed = (errorMessage?: string) =>
    ({
        type: CUSTOMER_CARD_APPLY_FAILED,
        payload: { errorMessage }
    } as const);

export const customerCardCancelled = () =>
    ({
        type: CUSTOMER_CARD_CANCELLED
    } as const);

export const saveParticipantsRSAA = (
    bffBaseUrl: string,
    dealId: string,
    dealerId: string,
    payload: SaveParticipantsPayload
): RSAACallAction => {
    // bankAccountNumber saves via this endpoint (not saveCustomerSpdRSAA) and only ever appears on the
    // buyer (BankInformation only renders for a business buyer, never the co-buyer). Stash it on the
    // SUCCESS type's meta so the SPD reducer can commit presence for it once this save confirms.
    const buyer = payload.buyer;
    const bankAccountNumberByCcid =
        buyer?.commonConsumerId && buyer.business && 'bankAccountNumber' in buyer.business
            ? { commonConsumerId: buyer.commonConsumerId, bankAccountNumber: buyer.business.bankAccountNumber }
            : undefined;
    const spdItems = payload.spd ?? [];

    return {
        [RSAA]: {
            endpoint: `${bffBaseUrl}/deal/${dealId}/participants`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-dr-dealerid': dealerId },
            body: JSON.stringify(payload),
            types: [
                SAVE_PARTICIPANTS_REQUEST,
                { type: SAVE_PARTICIPANTS_SUCCESS, meta: { spdItems, bankAccountNumberByCcid } },
                SAVE_PARTICIPANTS_FAILURE
            ]
        } satisfies RSAACallRequest
    } as RSAACallAction;
};

export const syncCustomerRSAA = (
    bffBaseUrl: string,
    dealId: string,
    dealerId: string,
    customers: SyncCustomerData[]
): RSAACallAction =>
    ({
        [RSAA]: {
            endpoint: `${bffBaseUrl}/sync-customer`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-dr-dealxgid': dealId, 'x-dr-dealerid': dealerId },
            body: JSON.stringify({ customers }),
            types: [SYNC_CUSTOMER_REQUEST, SYNC_CUSTOMER_SUCCESS, SYNC_CUSTOMER_FAILURE]
        } satisfies RSAACallRequest
    } as RSAACallAction);

export const setInsuranceProviders = (payload: InsuranceProviderOption[]) =>
    ({
        type: SET_INSURANCE_PROVIDERS,
        payload
    } as const);

export const fetchInsuranceProviders =
    () =>
    async (dispatch: (action: unknown) => void, getState: () => CustomerCardStoreState): Promise<void> => {
        const insuranceProviders = getInsuranceProviders(getState());
        if (insuranceProviders.length) return;

        const url = (window as any).jsonFileMap?.insuranceProviders as string | undefined;
        if (!url) return;

        const dealXgId = getDealId(getState());
        const dealerId = getDealerId(getState());

        try {
            const response = await restApiCaller.getResource<RawInsuranceProvider[]>(url);
            if (!response.ok || !response.response?.length) {
                logCustomerCardInsuranceFormEvent({ event: 'ProvidersLoad', status: 'failure', dealXgId, dealerId });
                return;
            }

            const formattedInsuranceProvidersData: InsuranceProviderOption[] = response.response.map((item) => ({
                label: item.Name,
                additionalText: [item.Address, item.City, `${item.State} ${item.ZIP}`, item.Phone].join(', '),
                ...item
            }));
            dispatch(setInsuranceProviders(formattedInsuranceProvidersData));
        } catch (error) {
            /* eslint-disable-next-line no-console */
            console.warn(`Failed to load Asset - ${url}`, error);
            logCustomerCardError('JsonLoadFailure', `Failed to fetch Insurance Providers data: ${url}, ${error}`);
            logCustomerCardInsuranceFormEvent({ event: 'ProvidersLoad', status: 'failure', dealXgId, dealerId });
        }
    };

export const filterInsurancePayload = (payload: VehicleInsurance): VehicleInsurance => {
    const filtered = { ...payload };
    if (!filtered.policyStartDate) delete filtered.policyStartDate;
    if (!filtered.policyExpirationDate) delete filtered.policyExpirationDate;
    if (filtered.agentAddress && !filtered.agentAddress.stateProvince) {
        filtered.agentAddress = { ...filtered.agentAddress };
        delete filtered.agentAddress.stateProvince;
    }
    if (!filtered.agentPhoneNumber) delete filtered.agentPhoneNumber;
    return filtered;
};

export const saveInsuranceRSAA = (
    bffBaseUrl: string,
    dealId: string,
    dealerId: string,
    payload: VehicleInsurance
): RSAACallAction =>
    ({
        [RSAA]: {
            endpoint: `${bffBaseUrl}/deal/${dealId}/vehicleInsurance`,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-dr-dealerid': dealerId },
            body: JSON.stringify(filterInsurancePayload(payload)),
            types: [SAVE_INSURANCE_REQUEST, SAVE_INSURANCE_SUCCESS, SAVE_INSURANCE_FAILURE]
        } satisfies RSAACallRequest
    } as RSAACallAction);

export type CustomerCardAction =
    | ReturnType<typeof seedCustomerCard>
    | ReturnType<typeof customerCardApplyStarted>
    | ReturnType<typeof customerCardApplySucceeded>
    | ReturnType<typeof customerCardApplyFailed>
    | ReturnType<typeof customerCardCancelled>
    | ReturnType<typeof setInsuranceProviders>
    // RSAA lifecycle actions dispatched by redux-api-middleware:
    | { type: typeof SAVE_PARTICIPANTS_REQUEST }
    | {
          type: typeof SAVE_PARTICIPANTS_SUCCESS;
          payload?: Record<string, unknown> | null;
          meta?: {
              spdItems?: CustomerSpdSaveItem[];
              bankAccountNumberByCcid?: { commonConsumerId: string; bankAccountNumber?: string };
          };
      }
    | { type: typeof SAVE_PARTICIPANTS_FAILURE; payload?: unknown }
    | { type: typeof SAVE_INSURANCE_REQUEST }
    | { type: typeof SAVE_INSURANCE_SUCCESS; payload?: Record<string, unknown> | null }
    | { type: typeof SAVE_INSURANCE_FAILURE; payload?: unknown };
